import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useModules, useToggleModule } from '@/lib/queries';
import { ApiError } from '@/lib/api';

const PHASE_LABELS: Record<string, string> = {
  tickets: 'Phase 1',
  users: 'Phase 1',
  roles: 'Phase 2',
  announcements: 'Phase 3',
};

const MODULE_DESCRIPTIONS: Record<string, string> = {
  tickets: 'Support tickets: embed panels, categories, auto-close, transcript.',
  users: 'User & purchases database with shop webhook integrations.',
  roles: 'Automatic role assignment: on join, after a delay, via inbound webhook.',
  announcements: 'Embed message builder for posting to channels as the bot.',
};

export function ModulesPage() {
  const modules = useModules();
  const toggle = useToggleModule();

  async function handleToggle(moduleId: string, enabled: boolean) {
    try {
      await toggle.mutateAsync({ moduleId, enabled });
      toast.success(`${moduleId}: ${enabled ? 'enabled' : 'disabled'}. Restart bot+api to apply.`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error';
      toast.error(msg);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Modules</h1>
        <p className="text-muted-foreground">
          Each module is independent. Disabling hides its UI and halts its processing.
        </p>
        <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-100">
          <strong>Restart required:</strong> after toggling, restart{' '}
          <code className="rounded bg-amber-500/20 px-1">apps/bot</code> and{' '}
          <code className="rounded bg-amber-500/20 px-1">apps/api</code> for the change to take
          effect. The sidebar only updates after the next sign-in.
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {modules.data?.map((m) => {
          const phase = PHASE_LABELS[m.moduleId] ?? 'Phase ?';
          return (
            <Card key={m.moduleId}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="capitalize">{m.moduleId}</CardTitle>
                    <CardDescription>{phase}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`toggle-${m.moduleId}`} className="text-xs">
                      {m.enabled ? 'ON' : 'OFF'}
                    </Label>
                    <Switch
                      id={`toggle-${m.moduleId}`}
                      checked={m.enabled}
                      disabled={toggle.isPending}
                      onCheckedChange={(v) => handleToggle(m.moduleId, v)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {MODULE_DESCRIPTIONS[m.moduleId] ?? 'Module description.'}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
